import {
  paymentInstrumentTypes,
  getSelectedCreditCardName,
  getCreditCardType,
  creditCardType,
} from '../../utils/common';
import LocaleKeys from '../../locales/keys';
import i18next from 'i18next';
import { exceptionalCountries } from '../../constants/exceptionalCountries';

const getAccountNumberDisplay = (type, card) => {
  if (type === paymentInstrumentTypes.creditCard) {
    return card?.AccountNumber || '';
  }
  return '';
};

const canEdit = (type) => type !== paymentInstrumentTypes.iTunesAccount;
const canDelete = function (type) {
  return (
    type !== paymentInstrumentTypes.iTunesAccount &&
    type !== paymentInstrumentTypes.externalBill
  );
};

const iconClassType = {
  paymentInstrumentIconTypeCard: 0,
  paymentInstrumentIconType5: 5,
  paymentInstrumentIconType4: 4,
  paymentInstrumentIconType6: 6,
};

const iconClass = (type, classType) => {
  const getSelectedName = getSelectedCreditCardName(type, classType);
  return `${i18next.t(
    LocaleKeys.subscriptionInfo.subscriptions[getSelectedName.cardName],
  )}`;
};

const getCardName = (cardType) =>
  Object.entries(creditCardType).reduce((all, item) => {
    const [name, type] = item;
    if (type === cardType) {
      all = name;
    }
    return all;
  }, 'CC');

const getExpirationDate = (expirationDateNode) => {
  const splitExpirationDate = expirationDateNode.split('/');
  const expirationMonth = splitExpirationDate[0];
  const expirationYear = `20${splitExpirationDate[1]}`;
  const expirationDate = `${expirationMonth}/${expirationYear}`;
  return {
    expirationMonth,
    expirationYear,
    expirationDate,
  };
};

const getCardTypeNode = (cardNumber, paymentInstrumentNode) => {
  if (cardNumber) {
    return getCreditCardType(cardNumber.toString())?.Type;
  }
  return paymentInstrumentNode?.CreditCard?.Type;
};

const getNameBasedOnWalletCollection = (
  walletCollection,
  currentPaymentInstrumentId,
  accountNumber,
  cardName,
) => {
  const getFirstSixDigitAccountNumber = accountNumber
    ?.toString()
    ?.substring(0, 6);
  const nickName = `${cardName} starting with ${getFirstSixDigitAccountNumber}`;

  const candidates = (walletCollection || []).reduce((all, item) => {
    if (item.Name === nickName) {
      all.push(item);
    }
    return all;
  }, []);

  const getDuplicateNickNameCount = candidates.filter(
    (item) => item.Id !== currentPaymentInstrumentId,
  ).length;

  return getDuplicateNickNameCount
    ? `${nickName} ${currentPaymentInstrumentId}`
    : nickName;
};

const getName = (
  cardNumber,
  paymentInstrumentCard,
  type,
  paymentInstrumentWalletCollectionNode,
) => {
  const cardName = getCardName(type);
  const { CreditCard, Id } = paymentInstrumentCard;
  if (cardNumber) {
    return {
      cardName: getNameBasedOnWalletCollection(
        paymentInstrumentWalletCollectionNode,
        Id,
        cardNumber,
        cardName,
      ),
    };
  }
  return {
    cardName: getNameBasedOnWalletCollection(
      paymentInstrumentWalletCollectionNode,
      Id,
      CreditCard?.AccountNumber,
      cardName,
    ),
  };
};

// Used for creating a new cyber source payment instrument
export const getUpdatedPaymentInstrumentPayload = (
  formValues,
  paymentInstrument,
  showAddNewAddress,
  postalCodeRegularExpression,
  paymentInstrumentWalletCollection,
) => {
  let setUpdatedFormValues;
  const {
    AddressLineOne,
    AddressLineTwo,
    City,
    Country,
    ExpirationDate,
    NameOnCard,
    PostalCode,
    Province,
    Region,
    SecurityCode,
    State,
    ZipCode,
    CardNumber,
  } = formValues;
  const { BillingAddress, ...paymentInstrumentNode } = paymentInstrument;
  const { expirationMonth, expirationYear, expirationDate } =
    getExpirationDate(ExpirationDate);
  const getTypePayload = getCardTypeNode(CardNumber, paymentInstrumentNode);
  const { cardName } = getName(
    CardNumber,
    paymentInstrumentNode,
    getTypePayload,
    paymentInstrumentWalletCollection,
  );

  const DefaultPayload = {
    AccountNumberDisplay: getAccountNumberDisplay(
      paymentInstrumentNode.Type,
      paymentInstrumentNode.CreditCard,
    ),
    CanDelete: canDelete(paymentInstrumentNode.Type),
    CanEdit: canEdit(paymentInstrumentNode.Type),
    CaptchaEnabledForCreate: false,
    CaptchaEnabledForEdit: false,
    CaptchaResponse: null,
    DisplayName: cardName,
    IconClass: iconClass(paymentInstrumentNode.Type, iconClassType),
    IsExpired: false,
    IsRestricted: false,
    Name: cardName,
  };

  const postalCodeFormatted = (postalCode) => {
    if (Country === 'USA' && postalCode && postalCode.length === 9) {
      postalCode = postalCode.replace(/(\w{5})(\w{4})/, '$1-$2');
    }
    return postalCode;
  };

  const BillingAddressPayload = {
    City: City,
    Country: Country,
    CountryHasStates: exceptionalCountries.includes(Country) ? true : false,
    County: null,
    Created: null,
    DefaultBilling: false,
    DefaultShipping: false,
    GeoCode: null,
    Latitude: null,
    LineOne: AddressLineOne,
    LineTwo: AddressLineTwo,
    Longitude: null,
    Name: NameOnCard,
    PhoneNumber: null,
    PostalCode: PostalCode || ZipCode,
    PostalCodeFormatted: postalCodeFormatted(PostalCode || ZipCode),
    PostalCodeRegex: postalCodeRegularExpression || null,
    ShipToName: null,
    State: exceptionalCountries.includes(Country)
      ? State || Province || Region
      : null,
    Status: 1,
    StatusName: null,
  };

  if (showAddNewAddress) {
    setUpdatedFormValues = {
      BillingAddress: BillingAddressPayload,
      ...paymentInstrumentNode,
      CreditCard: {
        ...paymentInstrumentNode.CreditCard,
        BillingAddressId: null,
        ExpirationDate: expirationDate,
        ExpirationMonth: expirationMonth,
        ExpirationYear: expirationYear,
        NameOnCard: NameOnCard,
        Cvv: SecurityCode,
        AccountNumber:
          CardNumber?.toString() ||
          paymentInstrumentNode?.CreditCard?.AccountNumber,
        Type: getTypePayload,
      },
      Default: true,
      ...DefaultPayload,
    };
  } else {
    setUpdatedFormValues = {
      ...paymentInstrumentNode,
      BillingAddressId: formValues.BillingAddress,
      CreditCard: {
        ...paymentInstrumentNode.CreditCard,
        ExpirationDate: expirationDate,
        ExpirationMonth: expirationMonth,
        ExpirationYear: expirationYear,
        NameOnCard: NameOnCard,
        Cvv: SecurityCode,
        AccountNumber:
          CardNumber?.toString() ||
          paymentInstrumentNode?.CreditCard?.AccountNumber,
        Type: getTypePayload,
      },
      ...DefaultPayload,
    };
  }

  return setUpdatedFormValues;
};
